import React, { useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { SiteConfigProvider } from './hooks/useSiteConfig.jsx';
import { GameProvider } from './Context/GameContext';
import { BettingProvider } from './Context/BettingContext';
import { ExposureProvider } from './hooks/useExposure';
import Layout from './components/layout/Layout';
import Splash from './pages/Splash/Splash';
import Home from './pages/Home/Home';
import Game from './pages/Game/Game';
import Settings from './pages/Settings/Settings';
import ProvablyFair from './pages/ProvablyFair/ProvablyFair';
import Sports from './pages/Sports/Sports';
import MatchDetail from './pages/Sports/components/MatchDetail/MatchDetail';
import AuthModal from './components/auth/AuthModal';
import WalletModal from './components/wallet/WalletModal';
import GlobalSearchModal from './components/common/GlobalSearchModal/GlobalSearchModal';
import ProfileModal from './components/profile/ProfileModal';
import BetHistoryModal from './components/betHistory/BetHistoryModal';
import SettingsModal from './components/settings/SettingsModal';
import NotificationModal from './components/notifications/NotificationModal';
import Menu from './pages/Menu/Menu.jsx';
import Promotions from './pages/Promotions/Promotions';
import Affiliate from './pages/Affiliate/Affiliate';
import VipClub from './pages/VipClub/VipClub';
import Bonus from './pages/Bonus/Bonus';
import Casino from './pages/Casino/Casino';
import Support from './pages/Support/Support';
import GiftCards from './pages/GiftCards/GiftCards';
import { useGamePrefetch } from './hooks/useGamePrefetch';

/**
 * Inner App Component - Has access to QueryClient
 */
function AppContent() {
  const [showSplash, setShowSplash] = useState(true);

  const handleSplashComplete = () => {
    // Hide splash when animation completes
    setShowSplash(false);
  };

  // Pre-fetch games in background for smooth search experience
  useGamePrefetch();

  return (
    <>
      {/* Splash Screen - Shows on every page refresh */}
      {showSplash && <Splash onComplete={handleSplashComplete} />}

            <Routes>
              {/* Main Layout Routes */}
              <Route path="/" element={<Layout />}>
                <Route index element={<Home />} />
                {/* Login route - shows home page with modal via query params */}
                <Route path="login" element={<Home />} />
                {/* Wallet route - opens wallet modal with query params: /wallet?mode=deposit|withdraw|swap */}
                <Route path="wallet" element={<Home />} />
                {/* Modal route - opens modals via query params */}
                <Route path="modal" element={<Home />} />
                <Route path="menu" element={<Menu />} />
                {/* Settings route - full page */}
                <Route path="settings" element={<Settings />} />
                <Route path="global-settings" element={<Settings />} />
                {/* Provably Fair / Help Center route */}
                <Route path="provably-fair" element={<ProvablyFair />} />
                {/* Promotions route */}
                <Route path="promotions" element={<Promotions />} />
                {/* Affiliate routes - must be before catch-all game route */}
                <Route path="affiliate/*" element={<Affiliate />} />
                {/* VIP Club route */}
                <Route path="vip-club" element={<VipClub />} />
                {/* Bonus route */}
                <Route path="bonus-page" element={<Bonus />} />
                {/* Casino routes */}
                <Route path="casino" element={<Casino />} />
                <Route path="casino/:category" element={<Casino />} />
                {/* Support route */}
                <Route path="support" element={<Support />} />
                {/* Gift Cards route */}
                <Route path="gift-cards" element={<GiftCards />} />
                {/* 
                  Game route - /providername/gameid
                  
                  This route handles all game launches:
                  - GIS games: /spribe/abc123, /pragmaticplay/xyz789, etc.
                  - JS games (E-sports): /esports/4ee8e0051a035b463b47c3c473ce317d
                  
                  Flow:
                  1. User clicks game → handleGameLaunch() or handleJsGameLaunch() called
                  2. Game API called → gets game URL
                  3. navigate(`/${providerName}/${gameId}`) → navigates to this route
                  4. <Game /> component receives params: { providername, gameid }
                  5. Game component displays iframe with game URL
                  
                  Example URLs:
                  - /spribe/crash-game-123 → GIS game
                  - /esports/4ee8e0051a035b463b47c3c473ce317d → E-sports (JS game)
                  
                  IMPORTANT: This catch-all route must be LAST to avoid matching other routes
                */}
                <Route path=":providername/:gameid" element={<Game />} />
                {/* Sports routes - order matters: specific routes first */}
                {/* Match detail route - must be before other sports routes to avoid conflicts */}
                <Route path="sports/markets/:matchId" element={<MatchDetail />} />
                {/* Specific sports routes */}
                <Route path="sports/all-sports" element={<Sports />} />
                <Route path="sports/popular" element={<Sports />} />
                {/* Generic sports routes - must come after specific routes */}
                <Route path="sports/:tab/:sport" element={<Sports />} />
                <Route path="sports/:tab" element={<Sports />} />
                <Route path="sports" element={<Sports />} />
                {/* More routes will be added here */}
              </Route>
            </Routes>

            {/* Auth Modal - Rendered outside routes to overlay everything */}
            <AuthModal />

            {/* Wallet Modal - Rendered outside routes to overlay everything */}
            <WalletModal />

            {/* Global Search Modal - Rendered outside routes to overlay everything */}
            <GlobalSearchModal />

            {/* Profile Modal - Rendered outside routes to overlay everything */}
            <ProfileModal />

            {/* Bet History Modal - Rendered outside routes to overlay everything */}
            <BetHistoryModal />

            {/* Settings Modal - Rendered outside routes to overlay everything */}
            <SettingsModal />

      {/* Notification Modal - Rendered outside routes to overlay everything */}
      <NotificationModal />
    </>
  );
}

/**
 * Main App Component
 * Sets up routing structure
 * Shows splash on every page refresh/reload as overlay
 * Wraps app with SiteConfigProvider for socket and config management
 * Wraps app with GameProvider for game search functionality
 */
function App() {
  return (
    <SiteConfigProvider>
      <ExposureProvider>
        <BettingProvider>
          <GameProvider>
            <AppContent />
          </GameProvider>
        </BettingProvider>
      </ExposureProvider>
    </SiteConfigProvider>
  );
}

export default App;
